import { useCallback, useEffect, useState } from 'react';
import { useWorkflowContext } from '../../contexts/WorkflowContext';
import { useNodeValidation, useNodeValidationPattern } from '../../hooks/useNodeValidation';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';
import type { CustomNodeData, LoopNodeConfig } from '../../types/nodes';
import { VariablePicker } from '../VariablePicker';

interface LoopConfigProps {
  nodeId: string;
  data: CustomNodeData;
  onChange: (data: CustomNodeData) => void;
  // Wizard state callbacks for parent component
  onWizardStateChange?: (state: {
    wizardMode: boolean;
    currentStep: number;
    totalSteps: number;
    canProceedToNext: boolean;
    onPreviousStep: () => void;
    onNextStep: () => void;
    onCompleteWizard: () => Promise<void>;
    validationAttempted: boolean;
    validationFailed: boolean;
  }) => void;
  onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

const defaultConfig: LoopNodeConfig = {
  arrayField: '',
  itemName: 'item'
};

export function LoopConfig({ nodeId, data, onChange, onWizardStateChange, onValidationRequest }: LoopConfigProps) {
  // Validation pattern implementation
  const {
    validationAttempted,
    validationFailed,
    performValidation,
    handleCompleteWizard,
  } = useNodeValidationPattern(nodeId, data);
  
  // Validation display state
  const { isValidating, lastValidationResult } = useNodeValidation();

  const { getAvailableFields, getFlattenedFields } = useWorkflowContext();
  const [config, setConfig] = useState<LoopNodeConfig>(() => {
    const existingConfig = (data as any).config as Partial<LoopNodeConfig>;
    return {
      ...defaultConfig,
      ...existingConfig,
    };
  });

  // Get available upstream fields that are arrays
  const availableFields = getAvailableFields(nodeId);
  const flattenedFields = getFlattenedFields(nodeId);
  const arrayFields = flattenedFields.filter(field => field.type === 'array');

  const handleChange = (newConfig: Partial<LoopNodeConfig>) => {
    const updatedConfig = { ...config, ...newConfig };
    setConfig(updatedConfig);
    onChange({
      ...data,
      config: updatedConfig,
    } as CustomNodeData);
  };

  // Register validation function with parent
  useEffect(() => {
    if (onValidationRequest) {
      onValidationRequest(performValidation);
    }
  }, [onValidationRequest, performValidation]);

  // Wizard state management
  useEffect(() => {
    if (onWizardStateChange) {
      onWizardStateChange({
        wizardMode: false, // Loop config doesn't use wizard mode
        currentStep: 1,
        totalSteps: 1,
        canProceedToNext: true,
        onPreviousStep: () => {},
        onNextStep: () => {},
        onCompleteWizard: async () => {
          await handleCompleteWizard(() => {
            // Save the current configuration
            onChange({
              ...data,
              config,
            } as CustomNodeData);
          });
        },
        validationAttempted,
        validationFailed,
      });
    }
  }, [onWizardStateChange, config, validationAttempted, validationFailed, handleCompleteWizard, onChange, data]);

  return (
    <div className="flex flex-col h-full">
      {/* Validation Results */}
      {(isValidating || lastValidationResult) && (
        <div className="px-6 py-4 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900">
          <ValidationResultDisplay
            errors={lastValidationResult?.errors || []}
            isValidating={isValidating}
          />
        </div>
      )}

      <div className="p-4 space-y-6">
        {/* Header */}
      <div>
        <h3 className="text-sm font-medium text-gray-900">Loop Configuration</h3>
        <p className="text-xs text-gray-500 mt-1">
          Iterate over an array and execute child nodes for each item
        </p>
      </div>

      {/* Available Array Fields */}
      {arrayFields.length > 0 && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="text-sm font-medium text-green-900 mb-2">Available Array Fields</h4>
          <div className="flex flex-wrap gap-1">
            {arrayFields.map((field) => (
              <button
                key={field.id}
                onClick={() => handleChange({ arrayField: field.id })}
                className={`px-2 py-1 text-xs border rounded transition-colors ${
                  config.arrayField === field.id
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-green-700 border-green-300 hover:bg-green-50'
                }`}
                title={`${field.description || field.label} - Click to select`}
              >
                {field.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Array Field Selection */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Array Field <span className="text-red-500">*</span>
        </label>
        <VariablePicker
          nodeId={nodeId}
          value={config.arrayField}
          onChange={(value) => handleChange({ arrayField: value })}
          placeholder="Select array field to iterate over (e.g., {{users}})"
          className="w-full text-sm"
        />
        <div className="text-xs text-gray-500">
          Choose an array field from upstream nodes to loop through
        </div>
      </div>

      {/* Item Variable Name */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Item Variable Name
        </label>
        <input
          type="text"
          value={config.itemName || 'item'}
          onChange={(e) => handleChange({ itemName: e.target.value })}
          placeholder="item"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <div className="text-xs text-gray-500">
          Child nodes can access the current item using {'{{' + (config.itemName || 'item') + '.fieldName}}'}
        </div>
      </div>

      {/* Configuration Preview */}
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Loop Behavior</h5>
        <div className="text-xs text-gray-600 space-y-1">
          <div><strong>Array:</strong> {config.arrayField || 'No array selected'}</div>
          <div><strong>Item Variable:</strong> {'{{' + (config.itemName || 'item') + '}}'}</div>
          <div><strong>Access Pattern:</strong> {'{{' + (config.itemName || 'item') + '.fieldName}}'}</div>
          <div className="mt-2 text-gray-500">
            Each child node will execute once for every item in the array
          </div>
        </div>
      </div>

      {/* Output Structure */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <h5 className="text-sm font-medium text-blue-900 mb-2">Output Structure</h5>
        <div className="text-xs text-blue-800 space-y-1">
          <div>After loop completion, downstream nodes can access:</div>
          <div className="ml-4 space-y-1 font-mono">
            <div>{'{{loop.results}}'} - Array of all loop iteration results</div>
            <div>{'{{loop.count}}'} - Total number of iterations</div>
            <div>{'{{loop.lastItem}}'} - The last item that was processed</div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
} 